import { ButtonHTMLAttributes, ReactNode } from 'react'
import { Loader2 } from 'lucide-react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  variant?: 'primary' | 'secondary' | 'danger' | 'accent'
  loading?: boolean
  icon?: ReactNode
}

export function Button({ 
  children, 
  variant = 'primary', 
  loading = false, 
  icon, 
  className = '', 
  disabled,
  ...props 
}: ButtonProps) {
  // At least 36 px high with a mouse and 44 px on a touch screen. The touch
  // size is repeated here because min-h-9 would otherwise beat the 44 px rule
  // in src/index.css, which has no specificity.
  const base = 'flex items-center justify-center gap-1.5 min-h-9 [@media(pointer:coarse)]:min-h-11 px-4 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-[0.99] disabled:opacity-50 disabled:pointer-events-none'
  
  const variants = {
    primary: 'bg-primary hover:bg-primary-hover text-white shadow-primary/10 hover:shadow-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
    accent: 'bg-accent hover:bg-accent/95 text-accent-foreground shadow-accent/10 hover:shadow-accent/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/20',
    secondary: 'border border-border hover:bg-surface-hover text-foreground-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border/50',
    danger: 'bg-rose-500 hover:bg-rose-600 text-white shadow-rose-500/10 hover:shadow-rose-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/20',
  }

  return (
    <button
      disabled={disabled || loading}
      className={`${base} ${variants[variant]} ${className}`}
      {...props}
    >
      {loading ? (
        <Loader2 size={13} className="animate-spin shrink-0" />
      ) : icon ? (
        <span className="shrink-0">{icon}</span>
      ) : null}
      <span>{children}</span>
    </button>
  )
}
